<ion-content>
  <div class="constrained-content">
    <ion-grid class="constrained-content">
      <ion-row>
        <ion-col size-md="8" size="12">
          <!-- Show loading state while initially loading -->
          <div *ngIf="isInitialLoading" class="loading-state">
            <ion-card class="outline-card loading-card">
              <ion-card-content>
                <ion-spinner
                  name="crescent"
                  class="loading-spinner"
                ></ion-spinner>
                <h2>Loading portal information...</h2>
                <p class="loading-text">
                  Please wait while we check your portal configuration.
                </p>
              </ion-card-content>
            </ion-card>
          </div>

          <!-- Portal Form (Create only) -->
          <div *ngIf="(!portal && !isInitialLoading) || isCreatingPortal">
            <ion-card class="portal-card">
              <ion-card-header>
                <ion-card-title>Portal Configuration Settings</ion-card-title>
                <div class="portal-docs-link">
                  <a
                    [href]="portalDocsUrl"
                    target="_blank"
                    class="learn-more-link"
                  >
                    <ion-icon name="information-circle-outline"></ion-icon>
                    {{
                      config.isExternal
                        ? "External portals are support portals for external customers"
                        : "Internal portals are support portals for internal employees"
                    }}
                  </a>
                </div>
              </ion-card-header>
              <ion-card-content>
                <form (ngSubmit)="submitPortalForm()" novalidate>
                  <div class="portal-details">
                    <!-- Logo and Primary Color Row -->
                    <div class="form-layout">
                      <div class="form-main-section">
                        <!-- Logo -->
                        <div class="detail-item logo-section">
                          <ion-label class="detail-label">
                            Logo
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.logo,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>
                          <div class="logo-upload-section">
                            <div class="logo-preview-container">
                              <div class="notification-icon logo-preview-box">
                                <!-- Loading state -->
                                <div
                                  *ngIf="isUploadingLogo"
                                  class="logo-loading"
                                >
                                  <ion-spinner
                                    name="crescent"
                                    class="logo-spinner"
                                  ></ion-spinner>
                                </div>
                                <!-- Upload button when no logo and not uploading -->
                                <ion-button
                                  slot="end"
                                  size="full"
                                  fill="clear"
                                  (click)="uploadPortalLogo()"
                                  class="icon-upload-button"
                                  *ngIf="
                                    !portalForm.supportLogo && !isUploadingLogo
                                  "
                                >
                                  <ion-icon
                                    name="cloud-upload-outline"
                                  ></ion-icon>
                                </ion-button>
                                <!-- Logo image when exists and not uploading -->
                                <img
                                  *ngIf="
                                    portalForm.supportLogo && !isUploadingLogo
                                  "
                                  [src]="portalForm.supportLogo"
                                  class="logo-image"
                                  (click)="uploadPortalLogo()"
                                  alt="Portal Logo"
                                />
                              </div>
                              <div
                                class="logo-actions"
                                *ngIf="portalForm.supportLogo"
                              >
                                <ion-button
                                  size="small"
                                  fill="clear"
                                  (click)="uploadPortalLogo()"
                                >
                                  <ion-icon
                                    name="cloud-upload-outline"
                                  ></ion-icon>
                                  <span>Upload new logo</span>
                                </ion-button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- Projects Section -->
                    <div class="projects-section">
                      <!-- Projects Title (always shown) -->
                      <div class="detail-item projects-section">
                        <ion-label class="detail-label">
                          Enabled Projects
                          <ion-icon
                            name="help-circle-outline"
                            class="help-icon"
                            ngxTippy
                            [tippyProps]="{
                              content: tooltips.projects,
                              placement: 'top',
                              arrow: false,
                              touch: false
                            }"
                          ></ion-icon>
                        </ion-label>

                        <!-- Projects List (when projects exist) -->
                        <div
                          class="projects-checkbox-list projects-list"
                          *ngIf="projects.length > 0"
                        >
                          <div
                            *ngFor="let project of projects"
                            class="project-checkbox-item project-item"
                            [class.disabled]="
                              hasProjectNoTicketTypes(project.id)
                            "
                          >
                            <ion-checkbox
                              [value]="project.id"
                              [checked]="
                                portalForm.selectedProjectIds.includes(
                                  project.id
                                )
                              "
                              [disabled]="hasProjectNoTicketTypes(project.id)"
                              (ionChange)="
                                onProjectCheckboxChange(project.id, $event)
                              "
                              class="project-checkbox"
                            ></ion-checkbox>
                            <ion-label class="project-label">{{
                              project.name
                            }}</ion-label>
                            <!-- Warning icon for projects without ticket types -->
                            <div
                              *ngIf="hasProjectNoTicketTypes(project.id)"
                              class="project-warning"
                            >
                              <ion-icon
                                name="warning-outline"
                                class="warning-icon project-warning-icon"
                              ></ion-icon>
                              <p>
                                Project has no ticket types - fix
                                <span
                                  (click)="navigateToTicketTypes(project.id)"
                                  class="clickable-link"
                                  >here</span
                                >
                              </p>
                            </div>
                          </div>
                        </div>

                        <!-- No Projects Warning (when no projects exist) -->
                        <div
                          class="warning-message"
                          *ngIf="projects.length === 0"
                        >
                          <ion-icon
                            name="warning-outline"
                            class="warning-icon"
                          ></ion-icon>
                          <span
                            >No {{ projectTypeName }} projects are
                            available.</span
                          >
                          <a
                            (click)="navigateToProjectCreation()"
                            class="project-link"
                          >
                            Create a {{ projectTypeName }} project
                          </a>
                        </div>
                      </div>
                    </div>
                    <!-- Slack Channel -->
                    <div class="slack-channel-section">
                      <div class="detail-item">
                        <ion-label class="detail-label">
                          Default Slack Channel
                          <ion-icon
                            name="help-circle-outline"
                            class="help-icon"
                            ngxTippy
                            [tippyProps]="{
                              content: tooltips.slackChannelId,
                              placement: 'top',
                              arrow: false,
                              touch: false
                            }"
                          ></ion-icon>
                        </ion-label>
                        <div class="slack-channel-input-container">
                          <!-- Channel selector -->
                          <div
                            (click)="
                              $event.preventDefault(); $event.stopPropagation()
                            "
                          >
                            <app-channel-selector
                              [channel]="selectedSlackChannel"
                              (channelDidChange)="onSlackChannelChanged($event)"
                              [initialButtonText]="'Select a Slack channel'"
                              [hideUnlinkButton]="true"
                              [hideLinkedToText]="false"
                            ></app-channel-selector>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Allow Direct Ticket Creation and Create Portal Button -->
                    <div class="toggle-save-section">
                      <div class="toggle-section">
                        <ion-label class="detail-label">
                          Allow Direct Ticket Creation
                          <ion-icon
                            name="help-circle-outline"
                            class="help-icon"
                            ngxTippy
                            [tippyProps]="{
                              content: tooltips.allowDirectTicketCreation,
                              placement: 'top',
                              arrow: false,
                              touch: false
                            }"
                          ></ion-icon>
                        </ion-label>
                        <label class="switch">
                          <input
                            type="checkbox"
                            [(ngModel)]="portalForm.allowDirectTicketCreation"
                            name="allowDirectTicketCreation"
                          />
                          <span class="slider"></span>
                        </label>
                      </div>
                      <ion-button
                        (click)="submitPortalForm()"
                        [disabled]="
                          isLoading ||
                          isDnsTesting ||
                          isTlsTesting ||
                          isUploadingLogo
                        "
                        size="default"
                      >
                        <ion-spinner
                          *ngIf="isLoading"
                          name="crescent"
                          class="loading-button-spinner"
                        ></ion-spinner>
                        <span *ngIf="!isLoading">Create Portal</span>
                      </ion-button>
                    </div>
                  </div>
                </form>
              </ion-card-content>
            </ion-card>
          </div>

          <!-- Show existing portal when portal exists -->
          <div *ngIf="!isInitialLoading && portal && !isCreatingPortal">
            <!-- Portal Details -->
            <div class="portal-details">
              <ion-card class="portal-card">
                <ion-card-header>
                  <ion-card-title>Portal Configuration Settings</ion-card-title>
                  <div class="portal-docs-link">
                    <a
                      [href]="portalDocsUrl"
                      target="_blank"
                      class="learn-more-link"
                    >
                      <ion-icon name="information-circle-outline"></ion-icon>
                      {{
                        config.isExternal
                          ? "External portals are support portals for external customers"
                          : "Internal portals are support portals for internal employees"
                      }}
                    </a>
                  </div>
                </ion-card-header>
                <ion-card-content>
                  <form (ngSubmit)="submitPortalForm()" novalidate>
                    <div class="portal-details">
                      <div class="form-layout">
                        <div class="form-main-section">
                          <!-- Logo and Primary Color Row -->
                          <div class="logo-color-row">
                            <!-- Logo -->
                            <div class="detail-item logo-section">
                              <ion-label class="detail-label">
                                Logo
                                <ion-icon
                                  name="help-circle-outline"
                                  class="help-icon"
                                  ngxTippy
                                  [tippyProps]="{
                                    content: tooltips.logo,
                                    placement: 'top',
                                    arrow: false,
                                    touch: false
                                  }"
                                ></ion-icon>
                              </ion-label>
                              <div class="logo-upload-section">
                                <div class="logo-preview-container">
                                  <div
                                    class="notification-icon logo-preview-box"
                                  >
                                    <!-- Loading state -->
                                    <div
                                      *ngIf="isUploadingLogo"
                                      class="logo-loading"
                                    >
                                      <ion-spinner
                                        name="crescent"
                                        class="logo-spinner"
                                      ></ion-spinner>
                                    </div>
                                    <!-- Upload button when no logo and not uploading -->
                                    <ion-button
                                      slot="end"
                                      size="full"
                                      fill="clear"
                                      (click)="uploadPortalLogo()"
                                      class="icon-upload-button"
                                      *ngIf="
                                        !portalForm.supportLogo &&
                                        !isUploadingLogo
                                      "
                                    >
                                      <ion-icon
                                        name="cloud-upload-outline"
                                      ></ion-icon>
                                    </ion-button>
                                    <!-- Logo image when exists and not uploading -->
                                    <img
                                      *ngIf="
                                        portalForm.supportLogo &&
                                        !isUploadingLogo
                                      "
                                      [src]="portalForm.supportLogo"
                                      class="logo-image"
                                      (click)="uploadPortalLogo()"
                                      alt="Portal Logo"
                                    />
                                  </div>
                                  <div
                                    class="logo-actions"
                                    *ngIf="portalForm.supportLogo"
                                  >
                                    <ion-button
                                      size="small"
                                      fill="clear"
                                      (click)="uploadPortalLogo()"
                                    >
                                      <ion-icon
                                        name="cloud-upload-outline"
                                      ></ion-icon>
                                      <span>Upload new logo</span>
                                    </ion-button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- Projects Section -->
                      <div class="projects-section">
                        <!-- Projects Title (always shown) -->
                        <div class="detail-item projects-section">
                          <ion-label class="detail-label">
                            Enabled Projects
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.projects,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>

                          <!-- Projects List (when projects exist) -->
                          <div
                            class="projects-checkbox-list projects-list"
                            *ngIf="projects.length > 0"
                          >
                            <div
                              *ngFor="let project of projects"
                              class="project-checkbox-item project-item"
                              [class.disabled]="
                                hasProjectNoTicketTypes(project.id)
                              "
                            >
                              <ion-checkbox
                                [value]="project.id"
                                [checked]="
                                  portalForm.selectedProjectIds.includes(
                                    project.id
                                  )
                                "
                                [disabled]="hasProjectNoTicketTypes(project.id)"
                                (ionChange)="
                                  onProjectCheckboxChange(project.id, $event)
                                "
                                class="project-checkbox"
                              ></ion-checkbox>
                              <ion-label class="project-label">{{
                                project.name
                              }}</ion-label>
                              <!-- Warning icon for projects without ticket types -->
                              <div
                                *ngIf="hasProjectNoTicketTypes(project.id)"
                                class="project-warning"
                              >
                                <ion-icon
                                  name="warning-outline"
                                  class="warning-icon project-warning-icon"
                                ></ion-icon>
                                <p>
                                  Project has no ticket types - fix
                                  <span
                                    (click)="navigateToTicketTypes(project.id)"
                                    class="clickable-link"
                                    >here</span
                                  >
                                </p>
                              </div>
                            </div>
                          </div>

                          <!-- No Projects Warning (when no projects exist) -->
                          <div
                            class="warning-message"
                            *ngIf="projects.length === 0"
                          >
                            <ion-icon
                              name="warning-outline"
                              class="warning-icon"
                            ></ion-icon>
                            <span
                              >No {{ projectTypeName }} projects are
                              available.</span
                            >
                            <a
                              (click)="navigateToProjectCreation()"
                              class="project-link"
                            >
                              Create a {{ projectTypeName }} project
                            </a>
                          </div>
                        </div>
                      </div>
                      <!-- Slack Channel -->
                      <div class="slack-channel-section">
                        <div class="detail-item">
                          <ion-label class="detail-label">
                            Default Slack Channel
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.slackChannelId,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>
                          <div class="slack-channel-input-container">
                            <!-- Channel selector -->
                            <div
                              (click)="
                                $event.preventDefault();
                                $event.stopPropagation()
                              "
                            >
                              <app-channel-selector
                                [channel]="selectedSlackChannel"
                                (channelDidChange)="
                                  onSlackChannelChanged($event)
                                "
                                [initialButtonText]="'Select a Slack channel'"
                                [hideUnlinkButton]="true"
                                [hideLinkedToText]="true"
                              ></app-channel-selector>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Allow Direct Ticket Creation and Save Button -->
                      <div class="toggle-save-section">
                        <div class="toggle-section">
                          <ion-label class="detail-label">
                            Allow Direct Ticket Creation
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.allowDirectTicketCreation,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>
                          <label class="switch">
                            <input
                              type="checkbox"
                              [(ngModel)]="portalForm.allowDirectTicketCreation"
                              name="allowDirectTicketCreation"
                            />
                            <span class="slider"></span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div class="save-buttons">
                      <ion-button
                        (click)="submitPortalForm()"
                        [disabled]="
                          isLoading ||
                          isDnsTesting ||
                          isTlsTesting ||
                          isUploadingLogo ||
                          !hasTopCardChanged
                        "
                        class="save-button"
                        [class.save-button--has-changes]="hasTopCardChanged"
                        size="default"
                      >
                        <ion-spinner
                          *ngIf="isLoading"
                          name="crescent"
                          class="loading-button-spinner"
                        ></ion-spinner>
                        <span *ngIf="!isLoading">Save</span>
                      </ion-button>
                      <ion-button
                        *ngIf="hasTopCardChanged"
                        type="button"
                        (click)="cancelPortalForm()"
                        fill="clear"
                        color="dark"
                        [disabled]="isLoading"
                      >
                        Clear Changes
                      </ion-button>
                    </div>
                  </form>
                </ion-card-content>
              </ion-card>

              <ion-card class="portal-card">
                <ion-card-header>
                  <ion-card-title>Customization</ion-card-title>
                </ion-card-header>
                <ion-card-content>
                  <!-- Portal Customization -->
                  <div class="portal-customization-section-container flexed">
                    <!-- Primary Color -->
                    <div class="detail-item color-section">
                      <ion-label class="detail-label">
                        Primary Color
                        <ion-icon
                          name="help-circle-outline"
                          class="help-icon"
                          ngxTippy
                          [tippyProps]="{
                            content: tooltips.primaryColor,
                            placement: 'top',
                            arrow: false,
                            touch: false
                          }"
                        ></ion-icon>
                      </ion-label>
                      <div class="color-upload-section">
                        <div
                          class="color-preview-container color-preview-wrapper"
                        >
                          <div
                            class="color-preview color-preview-box"
                            [style.background-color]="portalForm.primaryColor"
                          >
                            <input
                              #colorInput
                              type="color"
                              [(ngModel)]="portalForm.primaryColor"
                              name="primaryColor"
                              class="color-input"
                            />
                          </div>
                          <ion-button
                            size="small"
                            fill="clear"
                            (click)="colorInput.click()"
                            class="color-button"
                          >
                            <ion-icon name="color-palette-outline"></ion-icon>
                            <span>Change primary color</span>
                          </ion-button>
                        </div>
                      </div>
                    </div>
                    <!-- Dark Theme Accent Color -->
                    <div class="detail-item color-section">
                      <ion-label class="detail-label">
                        Dark Theme Accent Color
                        <ion-icon
                          name="help-circle-outline"
                          class="help-icon"
                          ngxTippy
                          [tippyProps]="{
                            content: tooltips.darkThemeAccentColor,
                            placement: 'top',
                            arrow: false,
                            touch: false
                          }"
                        ></ion-icon>
                      </ion-label>
                      <div class="color-upload-section">
                        <div
                          class="color-preview-container color-preview-wrapper"
                        >
                          <div
                            class="color-preview color-preview-box"
                            [style.background-color]="
                              portalForm.darkThemeAccentColor
                            "
                          >
                            <input
                              #darkColorInput
                              type="color"
                              [(ngModel)]="portalForm.darkThemeAccentColor"
                              name="darkThemeAccentColor"
                              class="color-input"
                            />
                          </div>
                          <ion-button
                            size="small"
                            fill="clear"
                            (click)="darkColorInput.click()"
                            class="color-button"
                          >
                            <ion-icon name="color-palette-outline"></ion-icon>
                            <span>Change accent color</span>
                          </ion-button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="portal-customization-section">
                    <!-- Featured Collections -->
                    <div class="detail-item">
                      <ion-label class="detail-label">
                        Featured Knowledge Base Collections
                        <ion-icon
                          name="help-circle-outline"
                          class="help-icon"
                          ngxTippy
                          [tippyProps]="{
                            content: tooltips.featuredCollections,
                            placement: 'top',
                            arrow: false,
                            touch: false
                          }"
                        ></ion-icon>
                      </ion-label>
                      <div class="featured-collections-container">
                        <div
                          class="featured-collections-list"
                          *ngIf="portalForm.featuredCollections.length > 0"
                        >
                          <ion-reorder-group
                            (ionItemReorder)="onReorder($event)"
                            [disabled]="false"
                          >
                            <div
                              *ngFor="
                                let collectionId of portalForm.featuredCollections;
                                let i = index;
                                trackBy: trackByCollectionId
                              "
                              class="featured-collection-item"
                            >
                              <ion-reorder slot="start"></ion-reorder>
                              <div class="collection-info">
                                <ion-label class="collection-name">
                                  {{ getCollectionName(collectionId) }}
                                </ion-label>
                                <ion-text color="medium" class="project-name">
                                  {{ getCollectionProjectName(collectionId) }}
                                </ion-text>
                              </div>
                              <ion-button
                                size="small"
                                fill="clear"
                                color="danger"
                                (click)="removeFeaturedCollection(collectionId)"
                                class="remove-button"
                              >
                                <ion-icon
                                  name="close-circle-outline"
                                ></ion-icon>
                                <span>Remove</span>
                              </ion-button>
                            </div>
                          </ion-reorder-group>
                        </div>
                        <ion-button
                          size="small"
                          fill="clear"
                          (click)="showCollectionSelector()"
                          class="add-button"
                        >
                          <ion-icon name="add-circle-outline"></ion-icon>
                          <span>Add Featured Collection</span>
                        </ion-button>
                      </div>
                    </div>

                    <!-- Custom Links -->
                    <div class="detail-item">
                      <ion-label class="detail-label">
                        Custom Links
                        <ion-icon
                          name="help-circle-outline"
                          class="help-icon"
                          ngxTippy
                          [tippyProps]="{
                            content: tooltips.customLinks,
                            placement: 'top',
                            arrow: false,
                            touch: false
                          }"
                        ></ion-icon>
                      </ion-label>
                      <div class="custom-links-container">
                        <div
                          class="links-list"
                          *ngIf="portalForm.customLinks.length > 0"
                        >
                          <div
                            *ngFor="
                              let link of portalForm.customLinks;
                              let i = index
                            "
                            class="link-item"
                          >
                            <ion-button
                              size="small"
                              fill="clear"
                              color="dark"
                              (click)="selectCustomLinkIcon(i)"
                              class="add-button"
                              ngxTippy
                              style="height: 50px; width: 50px"
                              [tippyProps]="{
                                content: 'Set icon for this link',
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            >
                              <div class="icon-preview">
                                <div *ngIf="getIconComponent(link.icon)">
                                  <lucide-icon
                                    [img]="getIconComponent(link.icon)"
                                    class="selected-icon"
                                  ></lucide-icon>
                                </div>
                              </div>
                            </ion-button>
                            <div class="link-inputs">
                              <ion-input
                                [(ngModel)]="link.title"
                                placeholder="Link title"
                                class="no-margin input-field"
                              ></ion-input>
                              <ion-input
                                [(ngModel)]="link.link"
                                placeholder="URL"
                                class="no-margin input-field"
                              ></ion-input>
                            </div>
                            <ion-button
                              size="small"
                              fill="clear"
                              color="dark"
                              (click)="removeCustomLink(i)"
                              class="item-accessory-button"
                            >
                              <ion-icon name="close-outline"></ion-icon>
                            </ion-button>
                          </div>
                        </div>
                        <ion-button
                          size="small"
                          fill="clear"
                          (click)="addCustomLink()"
                          class="add-button"
                        >
                          <ion-icon name="add-circle-outline"></ion-icon>
                          <span>Add Custom Link</span>
                        </ion-button>
                      </div>
                    </div>
                  </div>
                  <div class="deploy-actions-layout">
                    <!-- Status -->
                    <div class="deploy-info-section">
                      <!-- Custom Domain -->
                      <div class="detail-item">
                        <ion-label class="detail-label">
                          Custom Domain
                          <ion-icon
                            name="help-circle-outline"
                            class="help-icon"
                            ngxTippy
                            [tippyProps]="{
                              content: tooltips.customDomain,
                              placement: 'top',
                              arrow: false,
                              touch: false
                            }"
                          ></ion-icon>
                        </ion-label>

                        <!-- Custom Domain Input (always visible) -->
                        <div class="custom-domain-input-container">
                          <div class="input-container">
                            <ion-input
                              class="input-field custom-domain-input"
                              [(ngModel)]="portalForm.customDomain"
                              name="customDomain"
                              type="text"
                              placeholder="e.g. support.yourcompany.com"
                              (input)="onCustomDomainInput($event)"
                              (blur)="onCustomDomainInput($event)"
                              maxlength="253"
                              clearInput="true"
                              (ionClear)="clearCustomDomain()"
                            ></ion-input>
                          </div>
                          <div
                            class="custom-domain-help"
                            *ngIf="
                              portalForm.customDomain &&
                              (!portal || !portal.isEnabled)
                            "
                          >
                            <div class="dns-config-instructions">
                              <p class="dns-title">
                                DNS Configuration Required:
                              </p>
                              <p class="dns-step">
                                1. Add a CNAME record in your DNS provider:
                              </p>
                              <div class="dns-record-example">
                                <span class="dns-label">Name:</span>
                                <code>support</code> (or your preferred
                                subdomain)
                              </div>
                              <div class="dns-record-example">
                                <span class="dns-label">Value:</span>
                                <code>portal.{{ rootDomain }}</code>
                              </div>
                              <div class="dns-record-example">
                                <span class="dns-label">TTL:</span>
                                <code>3600</code> (or default)
                              </div>
                              <p class="dns-note">
                                Note: DNS changes can take up to 48 hours to
                                propagate globally.
                              </p>
                            </div>

                            <!-- Test Actions for Draft Portals -->
                            <div
                              class="test-actions"
                              *ngIf="
                                portalForm.customDomain &&
                                (!portal || !portal.isEnabled)
                              "
                            >
                              <ion-button
                                fill="outline"
                                color="tertiary"
                                (click)="testDnsConnection(portal)"
                                [disabled]="
                                  isLoading ||
                                  isDnsTesting ||
                                  isTlsTesting ||
                                  isUploadingLogo
                                "
                                class="test-button test-button--small"
                              >
                                <ion-spinner
                                  *ngIf="isDnsTesting"
                                  name="crescent"
                                  class="loading-button-spinner"
                                ></ion-spinner>
                                <ion-icon
                                  *ngIf="!isDnsTesting"
                                  name="checkmark-circle-outline"
                                ></ion-icon>
                                Test DNS
                              </ion-button>

                              <ion-button
                                fill="outline"
                                color="secondary"
                                (click)="testTlsCertificate(portal)"
                                [disabled]="
                                  isLoading ||
                                  isDnsTesting ||
                                  isTlsTesting ||
                                  isUploadingLogo
                                "
                                class="test-button test-button--small"
                              >
                                <ion-spinner
                                  *ngIf="isTlsTesting"
                                  name="crescent"
                                  class="loading-button-spinner"
                                ></ion-spinner>
                                <ion-icon
                                  *ngIf="!isTlsTesting"
                                  name="checkmark-circle-outline"
                                ></ion-icon>
                                Test TLS
                              </ion-button>
                            </div>

                            <!-- DNS Test Steps Progress -->
                            <div
                              class="dns-test-steps"
                              *ngIf="
                                isDnsTesting &&
                                currentTestingPortal?.id === portal.id
                              "
                            >
                              <div class="test-steps-container">
                                <div
                                  *ngFor="
                                    let step of dnsTestSteps;
                                    let i = index
                                  "
                                  class="test-step"
                                  [class.active]="i === currentDnsStep"
                                  [class.completed]="i < currentDnsStep"
                                >
                                  <div class="step-icon">
                                    <ion-icon
                                      *ngIf="i < currentDnsStep"
                                      name="checkmark-circle"
                                      color="success"
                                    ></ion-icon>
                                    <ion-icon
                                      *ngIf="i === currentDnsStep"
                                      name="ellipse"
                                      color="primary"
                                    ></ion-icon>
                                    <ion-icon
                                      *ngIf="i > currentDnsStep"
                                      name="ellipse-outline"
                                      color="medium"
                                    ></ion-icon>
                                  </div>
                                  <div class="step-text">{{ step }}</div>
                                </div>
                              </div>
                            </div>

                            <!-- TLS Test Steps Progress -->
                            <div
                              class="dns-test-steps"
                              *ngIf="
                                isTlsTesting &&
                                currentTestingPortal?.id === portal.id
                              "
                            >
                              <div class="test-steps-container">
                                <div
                                  *ngFor="
                                    let step of tlsTestSteps;
                                    let i = index
                                  "
                                  class="test-step"
                                  [class.active]="i === currentTlsStep"
                                  [class.completed]="i < currentTlsStep"
                                >
                                  <div class="step-icon">
                                    <ion-icon
                                      *ngIf="i < currentTlsStep"
                                      name="checkmark-circle"
                                      color="success"
                                    ></ion-icon>
                                    <ion-icon
                                      *ngIf="i === currentTlsStep"
                                      name="ellipse"
                                      color="primary"
                                    ></ion-icon>
                                    <ion-icon
                                      *ngIf="i > currentTlsStep"
                                      name="ellipse-outline"
                                      color="medium"
                                    ></ion-icon>
                                  </div>
                                  <div class="step-text">{{ step }}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Auth -->
                      <div class="detail-item">
                        <ion-label class="detail-label">
                          Authentication
                          <ion-icon
                            name="help-circle-outline"
                            class="help-icon"
                            ngxTippy
                            [tippyProps]="{
                              content: tooltips.signInEmailSender,
                              placement: 'top',
                              arrow: false,
                              touch: false
                            }"
                          ></ion-icon>
                        </ion-label>

                        <ion-card class="outline-card no-margin">
                          <ion-list>
                            <ion-item>
                              <ion-icon
                                name="mail-unread-outline"
                                slot="start"
                              ></ion-icon>
                              <ion-label text-wrap slot="start">
                                <h3>Magic link email verification</h3>
                                <p>
                                  We'll verify users via a custom link sent to
                                  their email.
                                </p>
                              </ion-label>
                              <ion-label slot="end">
                                <p>Sent from</p>
                                <ion-button
                                  fill="clear"
                                  size="small"
                                  color="dark"
                                  ngxTippy
                                  [tippyProps]="{
                                    content: 'Change sending email address',
                                    placement: 'top',
                                    arrow: false,
                                    touch: false
                                  }"
                                  (click)="
                                    emailSenderType === 'custom'
                                      ? (emailSenderType = 'default')
                                      : (emailSenderType = 'custom')
                                  "
                                >
                                  <code>{{ getSenderEmail() }}</code>
                                  <ion-icon name="pencil-outline"></ion-icon>
                                </ion-button>
                              </ion-label>
                            </ion-item>
                          </ion-list>
                        </ion-card>

                        <div class="email-sender-segment-container">
                          <!-- Custom email input when custom is selected -->
                          <div
                            *ngIf="emailSenderType === 'custom'"
                            class="email-domain-input-container"
                          >
                            <!-- Show input fields when verified domains exist -->
                            <div *ngIf="verifiedEmailDomains.length > 0">
                              <div class="email-input-flex">
                                <div class="email-prefix-container">
                                  <ion-input
                                    class="input-field email-prefix-input"
                                    [(ngModel)]="signInEmailPrefix"
                                    (ionInput)="onEmailPrefixChange($event)"
                                    name="signInEmailPrefix"
                                    type="text"
                                    placeholder="noreply"
                                    maxlength="64"
                                    clearInput="true"
                                  ></ion-input>
                                </div>
                                <div class="email-at-symbol">&#64;</div>
                                <div class="email-domain-container">
                                  <ion-select
                                    class="email-domain-select"
                                    [(ngModel)]="selectedEmailDomain"
                                    (ionChange)="onEmailDomainChange()"
                                    name="selectedEmailDomain"
                                    placeholder="Select domain"
                                    interface="popover"
                                  >
                                    <ion-select-option
                                      *ngFor="
                                        let domain of verifiedEmailDomains
                                      "
                                      [value]="domain.domain"
                                    >
                                      {{ domain.domain }}
                                    </ion-select-option>
                                  </ion-select>
                                  <div class="email-domain-cta">
                                    <ion-button
                                      fill="clear"
                                      size="small"
                                      color="primary"
                                      (click)="
                                        navigateToEmailDomainVerification()
                                      "
                                    >
                                      <ion-icon
                                        name="settings-outline"
                                        slot="start"
                                      ></ion-icon>
                                      Verify a new email domain
                                    </ion-button>
                                  </div>
                                </div>
                              </div>
                              <div
                                *ngIf="
                                  portalForm.signInEmailSender &&
                                  !isEmailFromVerifiedDomain(
                                    portalForm.signInEmailSender
                                  )
                                "
                                class="validation-error"
                              >
                                Email domain must be verified in your email
                                domain settings
                              </div>
                            </div>

                            <!-- Show setup message when no verified domains -->
                            <div
                              *ngIf="verifiedEmailDomains.length === 0"
                              class="email-setup-message"
                            >
                              <p>
                                No verified email domains found. Set up email
                                domain verification to configure sender emails.
                              </p>
                              <ion-button
                                fill="outline"
                                size="small"
                                color="primary"
                                (click)="navigateToEmailDomainVerification()"
                              >
                                <ion-icon
                                  name="settings-outline"
                                  slot="start"
                                ></ion-icon>
                                Set up email domain verification
                              </ion-button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="save-buttons">
                    <ion-button
                      (click)="submitPortalForm()"
                      [disabled]="
                        isLoading ||
                        isDnsTesting ||
                        isTlsTesting ||
                        isUploadingLogo ||
                        !hasPortalCustomizationChanged
                      "
                      class="save-button"
                      [class.save-button--has-changes]="
                        hasPortalCustomizationChanged
                      "
                      size="default"
                    >
                      <ion-spinner
                        *ngIf="isLoading"
                        name="crescent"
                        class="loading-button-spinner"
                      ></ion-spinner>
                      <span *ngIf="!isLoading">Save</span>
                    </ion-button>
                    <ion-button
                      *ngIf="hasPortalCustomizationChanged"
                      type="button"
                      (click)="revertPortalCustomizationChanges()"
                      fill="clear"
                      color="dark"
                      [disabled]="isLoading"
                    >
                      Clear Changes
                    </ion-button>
                  </div>
                </ion-card-content>
              </ion-card>

              <!-- Danger Zone -->
              <ion-card class="danger-zone-card">
                <ion-card-header>
                  <ion-card-title class="danger-zone-title">
                    <ion-icon name="warning-outline" color="danger"></ion-icon>
                    Danger Zone
                  </ion-card-title>
                </ion-card-header>
                <ion-card-content>
                  <div class="danger-zone-content">
                    <!-- Disable Portal for Live Portals -->
                    <div
                      class="danger-zone-item"
                      *ngIf="portal && portal.isEnabled"
                    >
                      <div class="danger-zone-info">
                        <h3>Disable Portal</h3>
                        <p>
                          Need to change custom domain settings? You'll need to
                          disable this portal first, then make your changes and
                          re-enable it.
                        </p>
                      </div>
                      <ion-button
                        fill="outline"
                        color="warning"
                        (click)="disablePortal(portal.id)"
                        [disabled]="
                          isLoading ||
                          isDnsTesting ||
                          isTlsTesting ||
                          isUploadingLogo
                        "
                        class="disable-portal-button"
                      >
                        <ion-icon
                          name="pause-circle-outline"
                          slot="start"
                        ></ion-icon>
                        Disable Portal
                      </ion-button>
                    </div>

                    <div class="danger-zone-item">
                      <div class="danger-zone-info">
                        <h3>Delete Portal</h3>
                        <p>
                          Permanently delete this portal and all its associated
                          data.<br />This action cannot be undone.
                        </p>
                      </div>
                      <ion-button
                        fill="outline"
                        color="danger"
                        (click)="deletePortal(portal.id)"
                        [disabled]="
                          isLoading ||
                          isDnsTesting ||
                          isTlsTesting ||
                          isUploadingLogo
                        "
                      >
                        <ion-icon name="trash-outline"></ion-icon>
                        Delete Portal
                      </ion-button>
                    </div>
                  </div>
                </ion-card-content>
              </ion-card>
            </div>
          </div>
        </ion-col>
        <ion-col size="4">
          <!-- Deploy & Manage Card -->
          <ion-card class="portal-card" style="position: sticky; top: 22px">
            <ion-card-header class="flex-space-between flexed-align-center">
              <ion-card-title>Deployment</ion-card-title>
              <div *ngIf="portal">
                <ion-icon
                  name="ellipse"
                  [color]="portal.isEnabled ? 'success' : 'warning'"
                  class="padded-icon"
                ></ion-icon>
                {{ portal.isEnabled ? "Live" : "Draft" }}
              </div>
            </ion-card-header>
            <ion-card-content>
              <!-- No Portal State -->
              <div class="deploy-actions-layout" *ngIf="!portal">
                <div class="empty-state-container">
                  <ion-text color="medium"
                    >No portal has been configured yet. Save your configuration
                    to create your first portal.</ion-text
                  >
                </div>
              </div>

              <!-- Portal Status (Draft or Live) -->
              <div class="deploy-actions-layout" *ngIf="portal">
                <!-- Status -->
                <div class="deploy-info-section">
                  <div class="detail-item">
                    <div class="portal-status-container">
                      <div
                        class="status-message status-message--draft"
                        *ngIf="!portal.isEnabled"
                      >
                        <p class="status-text">
                          Heads up! This portal is currently in draft mode and
                          is not yet accessible to users. Configure a custom
                          domain and deploy it below so users can access the
                          portal!
                        </p>
                        <!-- Publish Button - moved inside the warning message -->
                        <div class="main-cta-section">
                          <ion-button
                            (click)="publishPortal(portal.id)"
                            [disabled]="
                              isLoading ||
                              isDnsTesting ||
                              isTlsTesting ||
                              isUploadingLogo ||
                              isPublishing
                            "
                            size="large"
                          >
                            <ion-spinner
                              *ngIf="isPublishing"
                              name="crescent"
                              class="loading-button-spinner--right"
                            ></ion-spinner>
                            <ion-icon
                              name="rocket-outline"
                              *ngIf="!isPublishing"
                              class="padded-icon"
                            ></ion-icon>
                            Publish Portal
                          </ion-button>
                        </div>
                      </div>
                      <div
                        class="status-message status-message--live"
                        *ngIf="portal.isEnabled"
                      >
                        <p class="status-text status-text--live">
                          🎉 Woohoo! Your portal is live!
                          <br /><br />
                          Users can now access your knowledge base and submit
                          tickets at
                          <strong
                            ><a
                              [href]="generatePortalUrl(portal)"
                              target="_blank"
                              rel="noopener"
                              class="domain-link"
                              >{{ generatePortalUrl(portal) }}</a
                            ></strong
                          >
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>
    </ion-grid>
  </div>
</ion-content>
